<template>
  <div class="main">
    <div class="header">
      <a-button type="primary" @click="goBack">
        <i class="iconfont icon-fanhuiicon mr5"></i> 返回
      </a-button>
    </div>

    <a-card class="content-card" :bordered="false">
      <!-- 公告标题 -->
      <div class="announcement-detail-title">{{ announcement.title }}</div>

      <!-- 公告元信息 -->
      <div class="announcement-detail-meta">
        <span>发布时间：{{ formatDate(announcement.createTime) }}</span>
      </div>

      <!-- 公告内容 -->
      <div class="announcement-detail-content">
        {{ announcement.content }}
      </div>

      <!-- 附件区域 -->
      <div v-if="announcement.attachments && announcement.attachments.length > 0" class="announcement-attachment">
        <a-divider>附件</a-divider>
        <div class="attachment-list">
          <div v-for="(attachment, index) in announcement.attachments" :key="index" class="attachment-item">
            <div class="attachment-actions">
              <a-button type="link" size="small" @click="previewAttachment(attachment)">{{ getFileName(attachment) }}</a-button>
              <a-button type="link" size="small" @click="previewAttachment(attachment)">预览</a-button>
              <a-button type="link" size="small" @click="downloadAttachment(attachment)">下载</a-button>
            </div>
          </div>
        </div>
      </div>
    </a-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { message } from 'ant-design-vue';
import { getById } from "@/api/announcement/index.js";

const router = useRouter();
const route = useRoute();
const announcement = ref({});
const announcementId = route.query.id;

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '';
  // 根据实际日期格式进行调整
  return dateString;
}

// 返回上一页
const goBack = () => {
  //router.push('/announcement');
  router.back();
}

// 预览附件
const previewAttachment = (attachmentUrl) => {
  // 在新标签页中打开附件进行预览
  window.open(attachmentUrl, '_blank');
}

// 下载附件
const downloadAttachment = (attachmentUrl) => {
  try {
    // 获取文件名
    const fileName = getFileName(attachmentUrl);
    // 检查是否为PDF文件
    const isPdf = isPdfFile(attachmentUrl);
    // 对于PDF文件，使用fetch API获取文件并创建Blob进行下载
    if (isPdf) {
      fetch(attachmentUrl, {
        method: 'GET',
        credentials: 'include' // 包含cookie等凭证信息
      }).then(response => {
        if (!response.ok) {
          throw new Error('网络响应错误');
        }
        return response.blob();
      }).then(blob => {
        // 创建Blob URL
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        // 清理
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success('PDF文件开始下载');
      }).catch(error => {
        message.error('下载PDF文件失败');
        console.error('下载PDF文件出错:', error);
      });
    } else {
      // 非PDF文件，使用传统方式下载
      const link = document.createElement('a');
      link.href = attachmentUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('开始下载附件');
    }
  } catch (error) {
    message.error('下载附件失败');
    console.error('下载附件出错:', error);
  }
}

const isPdfFile = (url) => {
  if (!url) return false;
  // 将URL转换为小写，检查是否以.pdf结尾或URL中包含.pdf格式
  const lowerCaseUrl = url.toLowerCase();
  return lowerCaseUrl.endsWith('.pdf') ||
      (lowerCaseUrl.includes('.pdf?') ||
          lowerCaseUrl.includes('.pdf&'));
}

// 获取公告详情
const getAnnouncementDetail = async () => {
  try {

    const res = await getById(announcementId);
    if (res.code === 200 && res.data) {
      announcement.value = res.data;
    } else {
      message.error('获取公告详情失败');
    }
  } catch (error) {
    message.error('获取公告详情出错');
    console.error('获取公告详情出错:', error);
  }
};

const getFileName = (url) => {
  if (!url) return '附件';
  const pathParts = url.split('/');
  let fileName = pathParts[pathParts.length - 1];

  const queryIndex = fileName.indexOf('?');
  if (queryIndex !== -1) {
    fileName = fileName.substring(0, queryIndex);
  }

  const hashIndex = fileName.indexOf('#');
  if (hashIndex !== -1) {
    fileName = fileName.substring(0, hashIndex);
  }

  return fileName || '附件';
}

onMounted(async () => {
  if (announcementId) {
    await getAnnouncementDetail();
  } else {
    message.warning('未找到公告ID');
  }
});
</script>

<style lang="less" scoped>
.main {
  padding: 20px;
  min-height: calc(100vh - 70px);
  background-color: #ffffff;
}

.header {
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
}

.content-card {
  width: 100%;
  min-height: calc(100vh - 70px);
}

.content-card .ant-card-body {
  padding: 24px;
}

.announcement-detail-title {
  font-size: 20px;
  font-weight: 600;
  color: #1d2129;
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.announcement-detail-meta {
  text-align: center;
  margin-bottom: 24px;
  color: #545c64;
  font-size: 14px;
}

.announcement-detail-content {
  font-size: 16px;
  line-height: 1.8;
  color: #2c3e50;
  white-space: pre-wrap; /* 保留空白和换行 */
  word-wrap: break-word; /* 允许长单词换行 */
  min-height: 200px;
}

.announcement-attachment {
  margin-top: 32px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.attachment-list {
  margin-top: 10px;
  overflow: hidden;
}

.attachment-item {
  display: block;
  margin-bottom: 5px;
  clear: both;
}

.attachment-actions {
  display: inline-block;
  border-radius: 4px;
  margin-left: 32px;
}

</style>